export const STAGES = [
  { id: "outreach", label: "Outreach Contacts", color: "bg-stage-outreach text-ink-700" },
  { id: "invited", label: "Invited to DBS", color: "bg-stage-invited text-blue-900" },
  { id: "joined", label: "Joined DBS", color: "bg-stage-joined text-green-900" },
  { id: "active", label: "Active in DBS", color: "bg-stage-active text-amber-900" },
  { id: "multiplying", label: "Multiplying DBS", color: "bg-stage-multiplying text-red-900" },
] as const;

export type StageId = (typeof STAGES)[number]["id"];

export const FAITH_STATUSES = [
  { id: "exploring", label: "Exploring Jesus", dot: "bg-amber-500" },
  { id: "drifted", label: "Drifted from Jesus", dot: "bg-ink-400" },
  { id: "new_christian", label: "New Christian", dot: "bg-blue-500" },
  { id: "christian_not_baptised", label: "Existing Christian (Not Baptised)", dot: "bg-purple-500" },
  { id: "christian_baptised", label: "Existing Christian (Baptised)", dot: "bg-green-600" },
] as const;

// "Light" categories that describe the discipleship pathway someone is on.
// Yellow = spiritually open / exploratory (Stories of Hope)
// Green  = actively responding (Commands of Christ + Baptism)
// Blue   = ongoing Christian discipleship (multiplication, leadership)
export const DBS_TYPES = [
  {
    id: "yellow",
    label: "Yellow Light · Stories of Hope",
    short: "Yellow Light",
    blurb: "Spiritually open · conversational · exploratory",
    description: "For people who are spiritually open and exploring. Stories of Hope opens up gospel conversations without assuming faith.",
    chip: "bg-amber-100 text-amber-900",
    dot: "bg-amber-400",
  },
  {
    id: "green",
    label: "Green Light · Commands of Christ + Baptism",
    short: "Green Light",
    blurb: "Actively responding · obedience · baptism",
    description: "For people actively responding to Jesus. Walks them through obedience-based discipleship, baptism, and starting a Discovery Bible Study.",
    chip: "bg-green-100 text-green-900",
    dot: "bg-green-500",
  },
  {
    id: "blue",
    label: "Blue Light · Ongoing Discipleship",
    short: "Blue Light",
    blurb: "Multiplication · leadership · disciple-making rhythms",
    description: "For ongoing Christian discipleship. Continuing growth, multiplication, leadership development, and disciple-making rhythms.",
    chip: "bg-blue-100 text-blue-900",
    dot: "bg-blue-500",
  },
] as const;

export type DbsTypeId = (typeof DBS_TYPES)[number]["id"];

export function dbsTypeLabel(id: string | null | undefined): string {
  return DBS_TYPES.find((d) => d.id === id)?.short ?? "—";
}

// Lead pipeline (renamed for movement language). Order matters — used for
// progress / tabs / completion logic.
export const LEAD_STATUSES = [
  { id: "inbox",      label: "Inbox",      blurb: "Just arrived — awaiting first contact" },
  { id: "connecting", label: "Connecting", blurb: "Reached out, building relationship" },
  { id: "starting",   label: "Starting",   blurb: "About to start their first DBS" },
  { id: "active_dbs", label: "Active DBS", blurb: "In an active Discovery Bible Study" },
  { id: "completed",  label: "Completed",  blurb: "Walked through the journey" },
] as const;

export type LeadStatusId = (typeof LEAD_STATUSES)[number]["id"];

// When a lead reaches "Completed", capture what comes next.
export const LEAD_OUTCOMES = [
  { id: "ongoing",     label: "Ongoing Discipleship",            blurb: "Continuing growth in their walk." },
  { id: "multiplying", label: "Disciple Ready to Make Disciples", blurb: "Released to start multiplying." },
] as const;

export type LeadOutcomeId = (typeof LEAD_OUTCOMES)[number]["id"];

// Leader kinds — what role this leader plays in the movement.
//
//  disciple_maker    = Regular Disciple Maker (default; uses own contacts)
//  cdm               = Campaign Disciple Maker (vetted; receives campaign leads)
//  catalyst          = Disciple Making Catalyst (mobilises, multiplies, oversees)
//  campaign_manager  = Campaign Manager (assigns leads, runs campaigns)
export const LEADER_KINDS = [
  {
    id: "disciple_maker",
    label: "Disciple Maker",
    blurb: "Trained — disciples through their own Oikos and contacts.",
    chip: "bg-ink-100 text-ink-700",
    dot: "bg-ink-400",
  },
  {
    id: "cdm",
    label: "Campaign Disciple Maker",
    blurb: "Vetted — actively follows up campaign-generated referrals.",
    chip: "bg-brand-50 text-brand-700",
    dot: "bg-brand",
  },
  {
    id: "catalyst",
    label: "Disciple Making Catalyst",
    blurb: "Mobiliser — activates DBS movement through their network.",
    chip: "bg-purple-100 text-purple-800",
    dot: "bg-purple-500",
  },
  {
    id: "campaign_manager",
    label: "Campaign Manager",
    blurb: "Coordinator — runs campaigns, assigns leads, oversees follow-up.",
    chip: "bg-blue-100 text-blue-800",
    dot: "bg-blue-500",
  },
] as const;

export type LeaderKindId = (typeof LEADER_KINDS)[number]["id"];

export function leaderKindLabel(id: string | null | undefined): string {
  return LEADER_KINDS.find((k) => k.id === id)?.label ?? "Disciple Maker";
}

export function leadStatusLabel(id: string | null | undefined): string {
  return LEAD_STATUSES.find((s) => s.id === id)?.label ?? id ?? "—";
}

export function leadOutcomeLabel(id: string | null | undefined): string {
  return LEAD_OUTCOMES.find((o) => o.id === id)?.label ?? "—";
}

export type FaithStatus = (typeof FAITH_STATUSES)[number]["id"];

export function faithLabel(id: string) {
  return FAITH_STATUSES.find((s) => s.id === id)?.label ?? id;
}

export function stageLabel(id: string) {
  return STAGES.find((s) => s.id === id)?.label ?? id;
}

export function fmtDate(ts: number | string | undefined): string {
  if (!ts) return "—";
  const d = typeof ts === "number" ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
